// Der Stichwortkatalog in der Oberflaeche (ST-TAX-003, MVP-Kern): lesen,
// einen Hausbegriff vorschlagen, einen Vorschlag freigeben, einen Begriff
// beenden oder durch einen anderen ersetzen, nach Name und frueherem Namen
// suchen.
//
// FACHSPRACHE (NFR-UX-003): "Stichwort", "Stichwortliste", "Vorschlag",
// "freigeben", "beenden" -- nicht "Begriff", "Achse", "Entwurf", "Alias" oder
// eine Kennung; der alte Name heisst "frueherer Name". Ein Satz je Grundcode mit Problem, Wirkung und naechstem
// Schritt (NFR-UX-005).
//
// K6: ein neues Stichwort ist ein Vorschlag und nicht vergebbar, bis eine
// ANDERE Person ihn freigibt und bestaetigt, dass er ein Sortiment oder Thema
// beschreibt und kein Merkmal einer Person. K1: gruppenweite Stichworte sind
// hier nur zu sehen. K8: ersetzt wird nur innerhalb desselben Hauses und
// derselben Liste; der Server prueft es, die Auswahl bietet nur solche an.

import type { PlainMessage } from './access.ts';
import { readResource, sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';
import { artWort } from './haus-art.ts';

/** Abschrift von MAXIMUM_TERM_NAME_LENGTH (taxonomy-catalog-commands.ts); die Probe liest sie dort. */
export const MAXIMUM_TERM_NAME_LENGTH = 80;
/** Abschrift von MAXIMUM_TERM_DEFINITION_LENGTH. */
export const MAXIMUM_TERM_DEFINITION_LENGTH = 500;

/** Die waehlbaren Endgruende; 'replaced' entsteht nur mit einem Nachfolger. */
export const END_REASONS = Object.freeze([
  Object.freeze({ code: 'obsolete', label: 'Wird nicht mehr gebraucht' }),
  Object.freeze({ code: 'created_in_error', label: 'Versehentlich angelegt' }),
] as const);

const END_REASON_LABELS: Readonly<Record<string, string>> = Object.freeze({
  obsolete: 'wird nicht mehr gebraucht',
  created_in_error: 'versehentlich angelegt',
  replaced: 'durch ein anderes Stichwort ersetzt',
});

export interface CatalogTermView {
  readonly taxonomyTermId: string;
  readonly taxonomyId: string;
  readonly displayName: string;
  readonly definition: string | null;
  /** NULL: gruppenweit. */
  readonly operatingContextId: string | null;
  readonly termStatus: string;
  readonly endReasonCode: string | null;
  readonly endedAt: string | null;
  readonly lockVersion: number;
  readonly createdByMe: boolean;
  /** Gilt das Stichwort jetzt? 'active' allein heisst bei einer befristeten Geltung nicht vergebbar. */
  readonly inEffect: boolean;
  /**
   * Was diese Person an diesem Stichwort darf: der Server entscheidet mit demselben Claim wie die
   * Aenderungsroute (ADR-0063 Nachtrag Ziffer 5), das Web zeigt nur danach Knoepfe. canEnd traegt
   * Beenden, Verwerfen und Ersetzen.
   */
  readonly canApprove: boolean;
  readonly canEnd: boolean;
}

export interface CatalogView {
  readonly taxonomies: readonly { readonly taxonomyId: string; readonly displayName: string }[];
  readonly houses: readonly { readonly operatingContextId: string; readonly displayName: string }[];
  readonly terms: readonly CatalogTermView[];
  /** Opak: unveraendert als ?cursor= zurueckgeben, nie deuten. Fehlt er, ist alles geladen. */
  readonly nextCursor: string | undefined;
  /** Mehr eigene Haeuser, als die Antwort traegt: die Seite sagt es, statt still abzuschneiden. */
  readonly housesTruncated: boolean;
}

export type CatalogResult =
  | { readonly outcome: 'loaded'; readonly catalog: CatalogView }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

/** Was eine Zeile gerade ist -- daraus folgen Beschriftung und Aktionen. */
export type TermKind = 'groupwide' | 'active' | 'proposal-mine' | 'proposal-other' | 'ended';

export function termKind(term: CatalogTermView): TermKind {
  if (term.endReasonCode !== null) return 'ended';
  if (term.operatingContextId === null) return 'groupwide';
  if (term.termStatus === 'draft') return term.createdByMe ? 'proposal-mine' : 'proposal-other';
  return 'active';
}

// Kam keine Antwort, weiss der Browser beim Schreiben nicht, ob es gilt; beim Lesen ist nichts passiert.
// Die Lesefehler zeigt die Seite als Fehler, die offenen Schreibfaelle als Warnung.

/** Vorschlagen: dieselbe Kennung beim erneuten Absenden legt nichts doppelt an (ADR-0060). */
export const PROPOSAL_UNCERTAIN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob dein Vorschlag gespeichert wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Schick ihn mit denselben Angaben noch einmal ab; doppelt angelegt wird dabei nichts.',
  retryable: true,
});

/** Freigeben, Ersetzen, Beenden, Verwerfen, Zurueckziehen: lock_version laesst eine Wiederholung nichts doppelt tun. */
export const CHANGE_UNCERTAIN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Änderung gespeichert wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Lade den Katalog neu. Dann siehst du, was gilt.',
  retryable: true,
});

export const LOAD_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Der Katalog ließ sich nicht laden.',
  nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
  retryable: true,
});

export const MORE_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Die nächsten Stichworte ließen sich nicht laden. Die bisherigen bleiben stehen.',
  nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
  retryable: true,
});

export const SEARCH_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Die Suche ließ sich nicht ausführen.',
  nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
  retryable: true,
});

const GONE: PlainMessage = Object.freeze({
  headline: 'Dieses Stichwort gibt es für dich nicht mehr.',
  explanation: 'Es gehört zu einem Verlag, auf den du keinen Zugriff hast, oder die Stichwortliste ist nicht mehr pflegbar. Es wurde nichts geändert.',
  nextStep: 'Lade den Katalog neu.',
  retryable: true,
});

const OUTDATED: PlainMessage = Object.freeze({
  headline: 'Jemand hat dieses Stichwort gerade geändert.',
  explanation: 'Deine Aktion wurde nicht ausgeführt, damit nichts überschrieben wird.',
  nextStep: 'Lade den Katalog neu und prüfe den aktuellen Stand.',
  retryable: true,
});

export const CATALOG_MESSAGES: CommandMessages = Object.freeze({
  notFound: GONE,
  conflictFallback: OUTDATED,
  conflicts: Object.freeze({
    taxonomy_term_outdated: OUTDATED,
    taxonomy_term_already_ended: Object.freeze({
      headline: 'Dieses Stichwort ist schon beendet.',
      explanation: 'Es wurde inzwischen beendet oder verworfen — vielleicht in einem anderen Fenster.',
      nextStep: 'Lade den Katalog neu.',
      retryable: true,
    }),
    taxonomy_term_groupwide: Object.freeze({
      headline: 'Gruppenweite Stichworte pflegst du hier nicht.',
      explanation: 'Sie gelten in der ganzen Gruppe. Ändern wird sie die Datenpflege, sobald es diese Rolle gibt.',
      nextStep: 'Wende dich an die Datenverantwortlichen, wenn ein gruppenweites Stichwort nicht mehr passt.',
      retryable: false,
    }),
    taxonomy_term_name_taken: Object.freeze({
      headline: 'Ein Stichwort mit diesem Namen gibt es in dieser Liste schon.',
      explanation: 'Zwei gleichnamige Stichworte würden Auswertungen verfälschen. Es wurde nichts angelegt.',
      nextStep: 'Nutze das vorhandene Stichwort oder wähle einen Namen, der den Unterschied sagt.',
      retryable: false,
    }),
    taxonomy_term_second_person_required: Object.freeze({
      headline: 'Deinen eigenen Vorschlag gibt eine andere Person frei.',
      explanation: 'So prüfen zwei Menschen, dass ein Stichwort ein Sortiment oder Thema beschreibt und kein Merkmal einer Person.',
      nextStep: 'Bitte eine Kollegin oder einen Kollegen aus deinem Verlag, den Vorschlag im Stichwortkatalog freizugeben.',
      retryable: false,
    }),
    taxonomy_term_not_draft: Object.freeze({
      headline: 'Dieser Vorschlag ist schon freigegeben.',
      explanation: 'Das Stichwort ist bereits vergebbar.',
      nextStep: 'Lade den Katalog neu.',
      retryable: true,
    }),
    taxonomy_term_not_active: Object.freeze({
      headline: 'Nur ein freigegebenes Stichwort lässt sich ersetzen.',
      explanation: 'Einen Vorschlag verwirfst du, statt ihn zu ersetzen. Es wurde nichts geändert.',
      nextStep: 'Verwirf den Vorschlag oder lass ihn zuerst freigeben.',
      retryable: false,
    }),
    taxonomy_term_replacement_invalid: Object.freeze({
      headline: 'Durch dieses Stichwort lässt sich nicht ersetzen.',
      explanation: 'Das neue Stichwort muss aus derselben Liste und demselben Verlag stammen, freigegeben sein und selbst nicht schon ersetzt. Es wurde nichts geändert.',
      nextStep: 'Wähle ein anderes Stichwort oder lade den Katalog neu.',
      retryable: false,
    }),
    client_request_reused: OUTDATED,
    client_request_superseded: Object.freeze({
      headline: 'Dein Vorschlag ist schon angekommen und wurde inzwischen bearbeitet.',
      explanation: 'Es wurde kein zweiter Vorschlag angelegt.',
      nextStep: 'Lade den Katalog neu.',
      retryable: true,
    }),
  }),
});

/**
 * Freigeben, Beenden und Ersetzen verlangen bei LIAISON_CATALOG_ROLE_ENFORCED das Recht
 * "Datenpflege". Die Knoepfe folgen den Flags; ein 403 heisst, der Schalter wurde nach dem
 * Laden umgelegt. Der allgemeine Satz spraeche von einer "Ansicht".
 */
export const MAINTAIN_FORBIDDEN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Das darf nur jemand mit dem Recht „Datenpflege“.',
  explanation: 'Deine Änderung wurde nicht gespeichert.',
  nextStep: 'Lade den Katalog neu. Dann siehst du, was du hier tun kannst.',
  retryable: false,
});

export const MAINTAIN_MESSAGES: CommandMessages = Object.freeze({ ...CATALOG_MESSAGES, forbidden: MAINTAIN_FORBIDDEN_MESSAGE });

/**
 * Das Ersetzen hat zwei 404: die Quelle (taxonomy_term_not_found) und den
 * Nachfolger (taxonomy_term_replacement_not_found). Der Client haelt 404-Gruende
 * nicht auseinander, und fuer die Person ist "fremd" von "nicht vorhanden"
 * ohnehin nicht zu trennen; ein Satz nennt deshalb beide.
 */
export const REPLACE_MESSAGES: CommandMessages = Object.freeze({
  ...MAINTAIN_MESSAGES,
  notFound: Object.freeze({
    headline: 'Dieses Stichwort oder das neue Stichwort gibt es für dich nicht mehr.',
    explanation: 'Eines von beiden gehört zu einem Verlag, auf den du keinen Zugriff hast, oder wurde inzwischen entfernt. Es wurde nichts geändert.',
    nextStep: 'Lade den Katalog neu und wähle das neue Stichwort noch einmal.',
    retryable: true,
  }),
});

/**
 * Einmal je Liste, wenn ein aktives Hausstichwort keinen Pflegeknopf hat: sonst stuende die
 * Zeile ohne Knopf und ohne Grund da. Bei Schalter aus traegt jedes canEnd, der Satz entfaellt.
 */
export const STEWARD_CHANGE_NOTE = 'Ändern kann jemand mit dem Recht „Datenpflege“.';

export function needsStewardNote(terms: readonly CatalogTermView[]): boolean {
  return terms.some((term) => term.operatingContextId !== null && term.endReasonCode === null && term.termStatus === 'active' && !term.canEnd);
}

/** Der Satz zu "Für die ganze Gruppe", EINMAL je Liste statt in jeder Zeile. */
export const GROUPWIDE_NOTE = 'Stichworte „Für die ganze Gruppe“ gelten überall. Hier sind sie nicht änderbar.';

/** Die Sichtbarkeit der Aenderung: was die Zeile gerade ist, in Worten. */
/** ohneRecht: die Liste nennt das fehlende Recht schon (needsStewardNote); dann sagt der eigene Vorschlag, wer freigibt. */
export function termStatusText(term: CatalogTermView, houseName: string | undefined, ohneRecht = false): string {
  const kind = termKind(term);
  const house = houseName ?? artWort(null, 'deinem');
  switch (kind) {
    case 'groupwide':
      return 'Gilt für die ganze Gruppe. Hier nicht änderbar.';
    // Neben der Pille sagt der Satz nur, was sie nicht sagt: das Haus und die Folge.
    case 'active':
      return term.inEffect ? `Stichwort von ${house}.` : `Stichwort von ${house}; außerhalb seiner Gültigkeit nicht vergebbar.`;
    case 'proposal-mine':
      return ohneRecht ? `Für ${house}. Freigeben kann jemand anderes mit dem Recht „Datenpflege“.` : `Für ${house}. Freigeben kann nur eine andere Person.`;
    case 'proposal-other':
      return term.canApprove ? `Vorschlag für ${house}.` : `Vorschlag für ${house}. Freigeben kann jemand mit dem Recht „Datenpflege“.`;
    case 'ended': {
      const reason = END_REASON_LABELS[term.endReasonCode ?? ''] ?? 'beendet';
      const day = term.endedAt === null ? '' : ` am ${new Date(term.endedAt).toLocaleDateString('de-DE')}`;
      return `Beendet${day}: ${reason}. Kontakte, die es tragen, behalten es; neu vergeben lässt es sich nicht.`;
    }
  }
}

function asText(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function termOf(row: Record<string, unknown>): CatalogTermView | undefined {
  if (typeof row.taxonomyTermId !== 'string' || typeof row.displayName !== 'string' || typeof row.lockVersion !== 'number') {
    return undefined;
  }
  return {
    taxonomyTermId: row.taxonomyTermId,
    taxonomyId: asText(row.taxonomyId) ?? '',
    displayName: row.displayName,
    definition: asText(row.definition),
    operatingContextId: asText(row.operatingContextId),
    termStatus: asText(row.termStatus) ?? '',
    endReasonCode: asText(row.endReasonCode),
    endedAt: asText(row.endedAt),
    lockVersion: row.lockVersion,
    createdByMe: row.createdByMe === true,
    inEffect: row.inEffect === true,
    canApprove: row.canApprove === true,
    canEnd: row.canEnd === true,
  };
}

function named(value: unknown, key: string): readonly { readonly id: string; readonly displayName: string }[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    const id = row[key];
    return typeof id === 'string' && typeof row.displayName === 'string' ? [{ id, displayName: row.displayName }] : [];
  });
}

export async function fetchCatalog(options: { readonly baseUrl: string; readonly accessToken: string; readonly cursor?: string; readonly send?: SendLike }): Promise<CatalogResult> {
  const query = options.cursor === undefined ? '' : `?cursor=${encodeURIComponent(options.cursor)}`;
  const result = await readResource({ url: `${options.baseUrl}/taxonomy-catalog${query}`, accessToken: options.accessToken, ...(options.send ? { send: options.send } : {}) });
  if (result.outcome === 'not-found') return { outcome: 'rejected', message: GONE };
  if (result.outcome === 'rejected') return result;
  const body = result.body;
  const terms = Array.isArray(body.terms) ? body.terms.flatMap((row: unknown) => termOf((row ?? {}) as Record<string, unknown>) ?? []) : [];
  return {
    outcome: 'loaded',
    catalog: {
      taxonomies: named(body.taxonomies, 'taxonomyId').map(({ id, displayName }) => ({ taxonomyId: id, displayName })),
      houses: named(body.houses, 'operatingContextId').map(({ id, displayName }) => ({ operatingContextId: id, displayName })),
      terms,
      housesTruncated: body.housesTruncated === true,
      nextCursor: typeof body.nextCursor === 'string' ? body.nextCursor : undefined,
    },
  };
}

/**
 * Haengt die naechste Seite an. Listen und Haeuser stehen auf jeder Seite; es gilt die juengste.
 * Ein Stichwort, das zwischen zwei Seiten schon geladen war, steht nur einmal da, in der juengeren
 * Fassung an der alten Stelle.
 */
export function appendCatalogPage(bisher: CatalogView, neu: CatalogView): { readonly catalog: CatalogView; readonly added: number } {
  const juenger = new Map(neu.terms.map((term) => [term.taxonomyTermId, term]));
  const bekannt = new Set(bisher.terms.map((term) => term.taxonomyTermId));
  const dazu = neu.terms.filter((term) => !bekannt.has(term.taxonomyTermId));
  return {
    catalog: { ...neu, terms: [...bisher.terms.map((term) => juenger.get(term.taxonomyTermId) ?? term), ...dazu] },
    added: dazu.length,
  };
}

/** Die Ansage nach "Weitere laden". */
export function morePagesSentence(added: number): string {
  if (added === 0) return 'Keine weiteren Stichworte.';
  return added === 1 ? '1 weiteres Stichwort geladen.' : `${added.toLocaleString('de-DE')} weitere Stichworte geladen.`;
}

export type TermCommandResult = CommandOutcome<{ readonly termStatus: string }>;

const applied = (record: Record<string, unknown>) => ({ termStatus: asText(record.termStatus) ?? '' });

export function proposeTerm(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly input: { readonly taxonomyId: string; readonly operatingContextId: string; readonly displayName: string; readonly definition: string };
  readonly clientRequestId: string;
  readonly send?: SendLike;
}): Promise<TermCommandResult> {
  return sendCommand({
    url: `${options.baseUrl}/taxonomy-terms`,
    accessToken: options.accessToken,
    body: { ...options.input, clientRequestId: options.clientRequestId },
    messages: CATALOG_MESSAGES,
    applied,
    ...(options.send ? { send: options.send } : {}),
  });
}

export function approveProposal(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly term: CatalogTermView;
  readonly send?: SendLike;
}): Promise<TermCommandResult> {
  return sendCommand({
    url: `${options.baseUrl}/taxonomy-terms/${encodeURIComponent(options.term.taxonomyTermId)}/approve`,
    accessToken: options.accessToken,
    body: { lockVersion: options.term.lockVersion, confirmsTopicNotPersonalTrait: true },
    messages: MAINTAIN_MESSAGES,
    applied,
    ...(options.send ? { send: options.send } : {}),
  });
}

export function endCatalogTerm(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly term: CatalogTermView;
  readonly endReasonCode: string;
  readonly send?: SendLike;
}): Promise<TermCommandResult> {
  return sendCommand({
    url: `${options.baseUrl}/taxonomy-terms/${encodeURIComponent(options.term.taxonomyTermId)}/end`,
    accessToken: options.accessToken,
    body: { endReasonCode: options.endReasonCode, lockVersion: options.term.lockVersion },
    messages: MAINTAIN_MESSAGES,
    applied,
    ...(options.send ? { send: options.send } : {}),
  });
}

export function replaceCatalogTerm(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly term: CatalogTermView;
  readonly replacementTermId: string;
  readonly send?: SendLike;
}): Promise<TermCommandResult> {
  return sendCommand({
    url: `${options.baseUrl}/taxonomy-terms/${encodeURIComponent(options.term.taxonomyTermId)}/replace`,
    accessToken: options.accessToken,
    body: { replacementTermId: options.replacementTermId, lockVersion: options.term.lockVersion },
    messages: REPLACE_MESSAGES,
    applied,
    ...(options.send ? { send: options.send } : {}),
  });
}

export interface ReplacementChoice {
  readonly taxonomyTermId: string;
  readonly displayName: string;
}

/**
 * Die Stichworte, durch die sich `term` ersetzen laesst, mit den Regeln des
 * Servers (replaceTerm): freigegeben, in Geltung, gleiches Haus, gleiche Liste
 * (K8), nicht es selbst. Der Katalog traegt hoechstens eine Seite; weitere
 * Ziele kommen aus der Suche (`found`).
 */
export function replacementCandidates(
  term: CatalogTermView,
  terms: readonly CatalogTermView[],
  found: readonly CatalogHit[] = [],
): readonly ReplacementChoice[] {
  const choices = new Map<string, ReplacementChoice>();
  const offer = (other: {
    readonly taxonomyTermId: string;
    readonly displayName: string;
    readonly taxonomyId: string;
    readonly operatingContextId: string | null;
    readonly termStatus: string;
    readonly endReasonCode: string | null;
    readonly inEffect: boolean;
  }): void => {
    if (
      other.taxonomyTermId !== term.taxonomyTermId &&
      other.termStatus === 'active' &&
      other.endReasonCode === null &&
      other.inEffect &&
      other.taxonomyId === term.taxonomyId &&
      other.operatingContextId === term.operatingContextId
    ) {
      choices.set(other.taxonomyTermId, { taxonomyTermId: other.taxonomyTermId, displayName: other.displayName });
    }
  };
  terms.forEach(offer);
  found.forEach(offer);
  return [...choices.values()];
}

/** Abschrift von MINIMUM_SEARCH_LENGTH (taxonomy-catalog-commands.ts); die Probe liest sie dort. */
export const MINIMUM_SEARCH_LENGTH = 2;

export interface CatalogHit {
  readonly taxonomyTermId: string;
  readonly taxonomyId: string;
  /** Der Name der Liste; sie steht nicht unbedingt auf der geladenen Katalogseite. */
  readonly taxonomyDisplayName: string;
  readonly displayName: string;
  readonly definition: string | null;
  readonly operatingContextId: string | null;
  readonly termStatus: string;
  readonly endReasonCode: string | null;
  /** Der gefundene fruehere Name, wenn der Treffer ueber ihn kam. */
  readonly formerName: string | null;
  readonly inEffect: boolean;
}

export type SearchOutcome = CommandOutcome<{ readonly hits: readonly CatalogHit[]; readonly truncated: boolean }>;

function hitOf(row: Record<string, unknown>): CatalogHit | undefined {
  if (typeof row.taxonomyTermId !== 'string' || typeof row.displayName !== 'string') return undefined;
  return {
    taxonomyTermId: row.taxonomyTermId,
    taxonomyId: asText(row.taxonomyId) ?? '',
    taxonomyDisplayName: asText(row.taxonomyDisplayName) ?? '',
    displayName: row.displayName,
    definition: asText(row.definition),
    operatingContextId: asText(row.operatingContextId),
    termStatus: asText(row.termStatus) ?? '',
    endReasonCode: asText(row.endReasonCode),
    formerName: asText(row.formerName),
    inEffect: row.inEffect === true,
  };
}

export function searchCatalog(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly query: string;
  /** Nur vergebbare Stichworte, etwa fuer die Massenpflege. */
  readonly assignableOnly?: boolean;
  readonly send?: SendLike;
}): Promise<SearchOutcome> {
  return sendCommand({
    url: `${options.baseUrl}/taxonomy-terms/search`,
    accessToken: options.accessToken,
    body: { query: options.query, ...(options.assignableOnly === true ? { assignableOnly: true } : {}) },
    messages: CATALOG_MESSAGES,
    applied: (record) => ({
      hits: Array.isArray(record.hits) ? record.hits.flatMap((row: unknown) => hitOf((row ?? {}) as Record<string, unknown>) ?? []) : [],
      truncated: record.truncated === true,
    }),
    ...(options.send ? { send: options.send } : {}),
  });
}

/** Was ein Treffer gerade ist, in Worten. */
export function hitStatusText(hit: CatalogHit, houseName: string | undefined): string {
  if (hit.endReasonCode !== null) {
    return `Beendet: ${END_REASON_LABELS[hit.endReasonCode] ?? 'beendet'}. Neu vergeben lässt es sich nicht.`;
  }
  const scope = hit.operatingContextId === null ? 'Gilt für die ganze Gruppe.' : `Stichwort von ${houseName ?? artWort(null, 'deinem')}.`;
  if (hit.termStatus === 'draft') return `${scope} Noch ein Vorschlag, nicht vergebbar.`;
  return hit.inEffect ? `${scope} Vergebbar.` : `${scope} Gilt gerade nicht und ist deshalb nicht vergebbar.`;
}

/** Der Satz zur Trefferzahl; er steht in der Statusregion der Suche. */
export function searchSummary(count: number, truncated: boolean): string {
  if (count === 0) return 'Dazu gibt es kein Stichwort, auch nicht unter einem früheren Namen.';
  const zahl = count === 1 ? 'Ein Stichwort gefunden.' : `${count} Stichworte gefunden.`;
  return truncated ? `${zahl} Es gibt weitere; grenze die Suche ein.` : zahl;
}

/**
 * Der Satz zur Suche, wenn eine Liste gewaehlt ist: zaehlt nur deren Treffer. Die Suche selbst kennt
 * keine Liste; ist ihre Antwort gekuerzt, koennen Treffer der Liste hinter der Kuerzung liegen --
 * dann behauptet der Satz nicht, es gebe keine.
 */
export function listSearchSummary(count: number, truncated: boolean, inOtherLists: number, listChosen = true): string {
  if (listChosen && truncated) {
    if (count === 0) return 'Unter den ersten Treffern ist keiner aus dieser Liste. Grenze die Suche ein oder zeige alle Listen.';
    const zahl = count === 1 ? '1 Stichwort' : `${count.toLocaleString('de-DE')} Stichworte`;
    return `${zahl} unter den ersten Treffern. Grenze die Suche ein, um alle aus dieser Liste zu sehen.`;
  }
  if (count === 0 && inOtherLists > 0) return 'In dieser Liste gibt es dazu kein Stichwort.';
  return searchSummary(count, truncated);
}

/** Unter dem Suchsatz, wenn die gewaehlte Liste Treffer anderer Listen ausblendet. */
export function otherListsSentence(count: number): string {
  return count === 1 ? '1 weiteres Stichwort in einer anderen Liste.' : `${count.toLocaleString('de-DE')} weitere Stichworte in anderen Listen.`;
}

/**
 * Ueberschrift einer Liste mit ihrer Zahl, sichtbar "Genre (12+)", vorgelesen "Genre (12 oder mehr)",
 * solange der Katalog nicht alle Stichworte liefert. `count` ist, was die Seite unter der
 * Ueberschrift zeigt.
 */
export function listHeading(name: string, count: number, more: boolean): { readonly text: string; readonly spoken: string } {
  const zahl = count.toLocaleString('de-DE');
  return { text: `${name} (${zahl}${more ? '+' : ''})`, spoken: `${name} (${zahl}${more ? ' oder mehr' : ''})` };
}

/** Die Ansage nach der Wahl einer Liste. */
export function listChosenSentence(name: string | undefined, count: number, more: boolean): string {
  const zahl = `${count.toLocaleString('de-DE')}${more ? ' oder mehr' : ''}`;
  const worte = count === 1 && !more ? '1 Stichwort' : `${zahl} Stichworte`;
  return name === undefined ? `Alle Listen: ${worte}.` : `${name}: ${worte}.`;
}

/** Der Satz nach einem gelungenen Befehl; er steht in der Statusregion. */
export function appliedSentence(action: 'propose' | 'approve' | 'end' | 'discard' | 'withdraw', name: string): string;
export function appliedSentence(action: 'replace', name: string, successor: string): string;
export function appliedSentence(action: 'propose' | 'approve' | 'end' | 'discard' | 'withdraw' | 'replace', name: string, successor = ''): string {
  switch (action) {
    case 'propose':
      return `„${name}“ ist vorgeschlagen. Eine andere Person aus deinem Verlag gibt es frei; bis dahin lässt es sich nicht vergeben.`;
    case 'approve':
      return `„${name}“ ist freigegeben und ab jetzt vergebbar.`;
    case 'end':
      return `„${name}“ ist beendet. Kontakte, die es tragen, behalten es; neu vergeben lässt es sich nicht.`;
    // Verworfen wird ein fremder Vorschlag, zurueckgezogen der eigene.
    case 'discard':
      return `Der Vorschlag „${name}“ ist verworfen.`;
    case 'withdraw':
      return `Dein Vorschlag „${name}“ ist zurückgezogen.`;
    case 'replace':
      return `„${name}“ ist durch „${successor}“ ersetzt. Kontakte, die „${name}“ tragen, behalten es; die Suche nach dem alten Namen findet jetzt „${successor}“.`;
  }
}

/** Die bisherige Wahl, wenn es sie im neuen Katalog noch gibt; sonst die einzige Moeglichkeit oder keine. */
export function gueltigeWahl(bisher: string, moeglich: readonly string[]): string {
  if (bisher !== '' && moeglich.includes(bisher)) return bisher;
  return moeglich.length === 1 ? moeglich[0]! : '';
}

/**
 * Die Eingabe so, wie der Server sie vor dem Fingerprint normalisiert
 * (normalizeTermText: NFC, Rand weg, Leerraum verdichtet). Zwei Eingaben, die
 * der Server gleich sieht, halten damit dieselbe Kennung (ADR-0060).
 */
export function requestKeyInput(input: Readonly<Record<string, string>>): Record<string, string> {
  const text = (wert: string) => wert.normalize('NFC').trim().replace(/\s+/gu, ' ');
  return { ...input, displayName: text(input.displayName ?? ''), definition: text(input.definition ?? '') };
}
